import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Tag, Button, Modal, Checkbox, Space,
    message, Spin, Input, Typography, Tooltip, Badge,
} from 'antd';
import {
    EditOutlined, SearchOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Text } = Typography;
const { Search } = Input;

// Màu tag theo nhóm tổ hợp
const COMBO_COLOR = {
    A: 'blue',
    B: 'green',
    C: 'volcano',
    D: 'purple',
};

const getComboColor = (code = '') => COMBO_COLOR[code[0]] ?? 'default';

const SubjectCombinationManager = () => {
    const [subjects, setSubjects]               = useState([]);
    const [allCombos, setAllCombos]             = useState([]);
    const [loadingTable, setLoadingTable]       = useState(false);
    const [searchText, setSearchText]           = useState('');

    // Modal state
    const [modalOpen, setModalOpen]             = useState(false);
    const [modalSaving, setModalSaving]         = useState(false);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [checkedIds, setCheckedIds]           = useState([]);

    // ── Fetch danh sách ngành kèm tổ hợp (2 request, không N+1) ──────────
    const fetchSubjects = useCallback(async () => {
        setLoadingTable(true);
        try {
            const [subjectsRes, combosRes] = await Promise.all([
                axiosInstance.get('/api/v1/admin/subjects-with-combinations'),
                axiosInstance.get('/api/v1/admin/combination-configs'),
            ]);

            setAllCombos(combosRes.data?.data ?? []);
            setSubjects(subjectsRes.data?.data ?? []);
        } catch (err) {
            message.error('Không thể tải dữ liệu. Vui lòng thử lại.');
        } finally {
            setLoadingTable(false);
        }
    }, []);

    useEffect(() => { fetchSubjects(); }, [fetchSubjects]);

    // ── Mở modal gán tổ hợp ───────────────────────────────────────────────
    const openModal = (subject) => {
        setSelectedSubject(subject);
        setCheckedIds(subject.combination_configs?.map(c => c.id) ?? []);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedSubject(null);
        setCheckedIds([]);
    };

    // ── Lưu thay đổi ──────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!selectedSubject) return;
        setModalSaving(true);
        try {
            await axiosInstance.post(
                `/api/v1/admin/subjects/${selectedSubject.id}/combination-configs`,
                { combination_config_ids: checkedIds }
            );
            message.success('Cập nhật tổ hợp xét tuyển thành công!');
            closeModal();
            fetchSubjects();
        } catch (err) {
            message.error('Lưu thất bại. Vui lòng thử lại.');
        } finally {
            setModalSaving(false);
        }
    };

    // ── Lọc tìm kiếm ──────────────────────────────────────────────────────
    const filteredSubjects = subjects.filter(s =>
        !searchText ||
        s.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        s.code?.toLowerCase().includes(searchText.toLowerCase())
    );

    // ── Cột bảng ──────────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Mã ngành',
            dataIndex: 'code',
            key: 'code',
            width: 130,
            sorter: (a, b) => a.code?.localeCompare(b.code),
            render: (code) => <Text code>{code}</Text>,
        },
        {
            title: 'Tên ngành',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
            sorter: (a, b) => a.name?.localeCompare(b.name),
        },
        {
            title: 'Chương trình',
            dataIndex: ['subject_type', 'name'],
            key: 'subject_type',
            width: 220,
            ellipsis: true,
            render: (val) => val ?? <Text type="secondary">—</Text>,
        },
        {
            title: 'Tổ hợp xét tuyển',
            key: 'combinations',
            render: (_, record) => {
                const combos = record.combination_configs ?? [];
                if (combos.length === 0) {
                    return <Text type="secondary">Chưa có tổ hợp</Text>;
                }
                return (
                    <Space size={4} wrap>
                        {combos.map(c => (
                            <Tooltip key={c.id} title={c.name}>
                                <Tag color={getComboColor(c.code)}>{c.code}</Tag>
                            </Tooltip>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Số tổ hợp',
            key: 'count',
            width: 100,
            align: 'center',
            sorter: (a, b) =>
                (a.combination_configs?.length ?? 0) - (b.combination_configs?.length ?? 0),
            render: (_, record) => {
                const count = record.combination_configs?.length ?? 0;
                return (
                    <Badge
                        count={count}
                        showZero
                        color={count > 0 ? '#52c41a' : '#d9d9d9'}
                    />
                );
            },
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 130,
            align: 'center',
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openModal(record)}
                >
                    Gán tổ hợp
                </Button>
            ),
        },
    ];

    // ── Nội dung Modal ─────────────────────────────────────────────────────
    const checkboxOptions = allCombos.map(c => ({
        label: (
            <Space>
                <Tag color={getComboColor(c.code)} style={{ minWidth: 36, textAlign: 'center' }}>
                    {c.code}
                </Tag>
                <span>{c.name}</span>
            </Space>
        ),
        value: c.id,
    }));

    return (
        <AdminLayout title="Gán tổ hợp xét tuyển theo ngành">
            <Space style={{ marginBottom: 16 }}>
                <Search
                    placeholder="Tìm theo mã hoặc tên ngành..."
                    allowClear
                    prefix={<SearchOutlined />}
                    style={{ width: 300 }}
                    onSearch={setSearchText}
                    onChange={e => !e.target.value && setSearchText('')}
                />
            </Space>

            <Table
                columns={columns}
                dataSource={filteredSubjects}
                rowKey="id"
                loading={loadingTable}
                pagination={{
                    pageSize: 15,
                    showTotal: (total) => `Tổng ${total} ngành`,
                    showSizeChanger: false,
                }}
                scroll={{ x: 900 }}
            />

            {/* ── Modal gán tổ hợp ── */}
            <Modal
                title={
                    <Space>
                        <CheckCircleOutlined style={{ color: '#1890ff' }} />
                        <span>
                            Gán tổ hợp xét tuyển
                            {selectedSubject && (
                                <Text type="secondary" style={{ marginLeft: 8, fontWeight: 400, fontSize: 13 }}>
                                    — {selectedSubject.name}
                                </Text>
                            )}
                        </span>
                    </Space>
                }
                open={modalOpen}
                onCancel={closeModal}
                onOk={handleSave}
                okText="Lưu thay đổi"
                cancelText="Hủy"
                confirmLoading={modalSaving}
                width={520}
                destroyOnClose
            >
                {allCombos.length === 0 ? (
                    <Spin tip="Đang tải..." />
                ) : (
                    <>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                            Chọn tất cả tổ hợp mà ngành này chấp nhận. Bỏ chọn sẽ gỡ tổ hợp khỏi ngành.
                        </Text>
                        <Checkbox.Group
                            options={checkboxOptions}
                            value={checkedIds}
                            onChange={setCheckedIds}
                            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                        />
                        <Text
                            type="secondary"
                            style={{ display: 'block', marginTop: 16, fontSize: 12 }}
                        >
                            Đã chọn: <strong>{checkedIds.length}</strong> / {allCombos.length} tổ hợp
                        </Text>
                    </>
                )}
            </Modal>
        </AdminLayout>
    );
};

export default SubjectCombinationManager;
